import { Injectable } from '@angular/core';
import { Initializable } from '../../app/services/app-init.service';

import { EasyDebugDecorator } from '../../app/decorators/easy-debug.decorator';

import { AppInitService } from '../services/app-init.service';
import { ToasterService } from '@commons/services';
import { ModalController } from '@ionic/angular/standalone';
import { ModalErrorComponent } from '../modals/modal-error/modal-error.component';
import { ConnectionStatusEnum, NetworkStatusService } from './network-status.service';

/*
Services addError mais non traités ici car traités localement sur la page :
- getSeries
*/

@Injectable({ providedIn: 'root' })
@EasyDebugDecorator
export class UserErrorHandlerService implements Initializable {
  private static readonly CRITICAL_SERVICES = ['student', 'user_properties', 'gifts', 'payments', 'coupons'];
  errorCatched = [];
  initDone = false;

  isCriticalFlow = false;
  siteOpenerFailed = false;

  seriesListFailed = false;
  getSerieFailed = false;

  badgesWarningDone = false;
  sessionsWarningDone = false;
  examCodeWarningDone = false;
  drivingExamWarningDone = false;

  constructor(
    private appInitService: AppInitService,
    private toasterService: ToasterService,
    private modalController: ModalController,
    private networkStatusService: NetworkStatusService
  ) {
    this.appInitService.onStepChange().subscribe((state) => {
      if (state?.initDone) {
        this.initDone = true;
        this.handleErrors();
      }
    });
  }

  async init(): Promise<string> {
    return 'UserErrorHandler done';
  }

  async addError(errorCatched: object) {
    // Skip non-critical service errors when offline — the HTTP interceptor already
    // shows the relevant "Tu es hors ligne" toast.
    // Critical errors (student, payments, etc.) must still be queued so that
    // showCriticError() can display the initError modal.
    const isCritical = UserErrorHandlerService.CRITICAL_SERVICES.includes((errorCatched as any)?.service);
    if (!isCritical && this.networkStatusService.networkStatus === ConnectionStatusEnum.Offline) {
      return;
    }
    // Push synchronously so errors are in the queue when initDone fires.
    // The async getTop() check only gates whether we process errors now
    // (they'll be processed by handleErrors() on initDone regardless).
    this.errorCatched.push(errorCatched);
    const modalOpened = await this.modalController.getTop();
    if (!modalOpened && this.initDone && !this.isCriticalFlow) {
      this.handleErrors();
    }
  }

  resetErrors() {
    this.errorCatched = [];
  }

  handleErrors() {
    const isOffline = this.networkStatusService.networkStatus === ConnectionStatusEnum.Offline;
    // When offline, only keep critical errors (student, payments, etc.) so the
    // initError modal can be shown. Non-critical errors are dropped because
    // the HTTP interceptor already shows the "Tu es hors ligne" toast.
    if (isOffline) {
      this.errorCatched = this.errorCatched.filter((e) =>
        UserErrorHandlerService.CRITICAL_SERVICES.includes(e.service)
      );
      if (this.errorCatched.length === 0) {
        return;
      }
    }
    if (!!this.errorCatched && this.errorCatched.length > 0) {
      this.errorCatched.sort((a, b) => (a.criticity > b.criticity ? 1 : b.criticity > a.criticity ? -1 : 0));
      for (const error of this.errorCatched) {
        if (UserErrorHandlerService.CRITICAL_SERVICES.includes(error.service)) {
          this.showCriticError(error);
          break;
        } else {
          if (error.service === 'getSeries' || error.service === 'getSeriesList') {
            this.seriesListFailed = true;
          }
          if (error.service === 'fetchSerie') {
            this.getSerieFailed = true;
          }
          if (error.service === 'siteOpener' || error.service === 'getSecuredUrl') {
            this.siteOpenerFailed = true;
            this.openModalPack('unavaibleExternalContent', true, error);
          }
          if (error.service === 'sync-cdr') {
            const toastConf = {
              text: `Nous ne sommes pas parvenus à synchroniser tes données`,
              bgcolor: 'var(--color-danger)',
              duration: '5000',
            };
            this.launchToaster(toastConf);
          }
          if (error.service === 'badges' && !this.badgesWarningDone) {
            this.badgesWarningDone = true;
            const toastConf = {
              text: `Nous ne sommes pas parvenus à récupérer tes réussites les plus récentes. Le contenu affiché peut ne pas être à jour`,
              bgcolor: 'var(--color-danger)',
              duration: '5000',
            };
            this.launchToaster(toastConf);
          }
          if (!this.seriesListFailed && error.service === 'fetchSessions' && !this.sessionsWarningDone) {
            this.sessionsWarningDone = true;
            const toastConf = {
              text: `Nous ne sommes pas parvenus à récupérer l'historique de tes séries. Le contenu affiché peut ne pas être à jour`,
              bgcolor: 'var(--color-danger)',
              duration: '5000',
            };
            this.launchToaster(toastConf);
          }
          if (!this.seriesListFailed && error.service === 'fetchSession') {
            const toastConf = {
              text: `Nous ne sommes pas parvenus à récupérer l'historique de ta série`,
              bgcolor: 'var(--color-danger)',
              duration: '5000',
            };
            this.launchToaster(toastConf);
          }
          if (error.service === 'fetchSerie' || error.service === 'getSerie') {
            const toastConf = {
              text: `Nous ne sommes pas parvenus à récupérer cette série`,
              bgcolor: 'var(--color-danger)',
              duration: '5000',
            };
            this.launchToaster(toastConf);
          }
          if (!this.examCodeWarningDone && error.service === 'fetchExamenCodeByUserId') {
            this.examCodeWarningDone = true;
            const toastConf = {
              text: `Nous ne sommes pas parvenus à récupérer tes informations liées à l'examen du code`,
              bgcolor: 'var(--color-danger)',
              duration: '5000',
            };
            this.launchToaster(toastConf);
          }
          if (!this.drivingExamWarningDone && error.service === 'fetchDrivingExamenById') {
            this.drivingExamWarningDone = true;
            const toastConf = {
              text: `Nous ne sommes pas parvenus à récupérer tes données liées à l'examen de la conduite. Le contenu affiché peut ne pas être à jour`,
              bgcolor: 'var(--color-danger)',
              duration: '5000',
            };
            this.launchToaster(toastConf);
          }
          this.resetErrors();
          break;
        }
      }
    }
  }

  showCriticError(error: any) {
    // Prevent stacking multiple initError modals on repeated failures
    if (this.isCriticalFlow) {
      this.resetErrors();
      return;
    }
    this.isCriticalFlow = true;
    this.openModalPack('initError', true, error);
    this.resetErrors();
  }

  launchToaster(config: object) {
    this.toasterService.create(config);
  }

  async openModalPack(type?: string, noClose = false, error = null, customClass = '') {
    // evs-global.scss : small-modal | medium-modal
    if (!type) {
      type = 'marketingRegular';
    }
    const props = {
      type: type,
      error: error,
      closable: !noClose,
      bannerInfo: null,
    };
    const modal = await this.modalController.create({
      component: ModalErrorComponent,
      cssClass: customClass,
      backdropDismiss: noClose ? false : true,
      componentProps: props,
    });
    await modal.present();

    // Always reset state on dismiss — if the user tapped "Réessayer" we re-run init,
    // otherwise we just unlock the flow so the app isn't stuck.
    const { role } = await modal.onDidDismiss();
    this.isCriticalFlow = false;
    this.resetErrors();
    if (role === 'retry') {
      await this.appInitService.run();
    }
  }
}
